import type { FilterSelectionsMap } from '../../../filters/types/filters';
import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import { DataType, type SingleDataPoint } from '../../DataProvider/types';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type { PlotParameter } from '../../PageConfigProvider/types/item';
import { isParameterSelectionEqualToDataPoint } from './check-datapoint-in-parameter';
import { getParameterAggregatedData } from './get-parameter-aggregated-data';
import { getPrimaryParameterSelections } from './get-primary-parameter-selections';

/**
 * Returns true when the dataset's dimensions overlap with the primary
 * parameter selections — meaning aggregation is meaningful.
 */
const hasParameterOverlap = (
  dataset: { type: DataType },
  primarySelections: ReturnType<typeof getPrimaryParameterSelections>
): boolean => {
  if (!primarySelections?.length) return false;

  if (dataset.type === DataType.SINGLE && 'data' in dataset) {
    const points = (dataset as { data: SingleDataPoint[] }).data;
    return points.some((point) =>
      primarySelections.some((p) =>
        isParameterSelectionEqualToDataPoint(p, point)
      )
    );
  }

  return true;
};

/**
 * Processes multi-metric data by applying parameter aggregation to each dataset
 * @param byMetric - The raw multi-metric data map
 * @param plotParameters - Plot parameters from the chart configuration
 * @param reportParameters - Report parameters from the page configuration
 * @param activeFilterMap - Current active filters
 * @returns Processed MultiMetricDataMap with aggregated data
 */
export const processMultiMetricData = ({
  byMetric,
  plotParameters,
  reportParameters,
  activeFilterMap,
}: {
  byMetric: MultiMetricDataMap;
  plotParameters: PlotParameter[];
  reportParameters: ReportParameterConfig[];
  activeFilterMap: FilterSelectionsMap;
}): MultiMetricDataMap => {
  const primarySelections = getPrimaryParameterSelections({
    reportParameters,
    activeFilterMap,
  });

  const next: MultiMetricDataMap = {};
  (Object.keys(byMetric) as (keyof MultiMetricDataMap)[]).forEach(
    (metricKey) => {
      const dataset = byMetric[metricKey];
      next[metricKey] =
        dataset && hasParameterOverlap(dataset, primarySelections)
          ? getParameterAggregatedData({
              data: dataset,
              plotParameters,
              reportParameters,
              activeFilterMap,
            })
          : dataset;
    }
  );
  return next;
};
